/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/jsx-no-bind */
import React, { useState } from 'react';
import { useClipboard } from '@mantine/hooks';
import { Col, Row, Spinner, Button, Modal, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { HiOutlineClipboardCopy, HiOutlineCreditCard } from 'react-icons/hi';
import { ReactComponent as BarcodeSvg } from '../../../../assets/icons/barcode.svg';
import PaymentSessionService from '../../../../services/http/paymentSessionService';
import PaymentHttpService from '../../../../services/http/payment-http';
import {
    StyledPageSubTitle,
    StyledPageTitle,
} from '../../../../styles/pageTitle';
import { formatCurrency } from '../../../../utils/strings';
import InvoiceSummary from '../../InvoiceSummary';
import handleResponseError from '../../../../utils/handleResponseError';
import PaymentModal from '../../../PaymentForm/PaymentModal';
import {
    buildPaymentsPayload,
    isAlreadyPaidMessage,
    PaymentMethodsFormSubmitData,
} from '../../../../services/pagarme';
import { getFormPaymentMethod } from '../../../../utils/getFormPaymentMethod';
import { isPaymentDeadlineExpired } from '../../../../utils/isPaymentDeadlineExpired';
import { getInvoicePaymentBlockReason } from '../../../../utils/getInvoicePaymentBlockReason';

const PRECONDITION_FAILED = 412;

const SessionPayment = (props: any) => {
    const [generatingLink, setGeneratingLink] = useState(false);
    const [copiedLink, setCopiedLink] = useState<{
        paymentUrl: string;
        expiresAt: string;
    } | null>(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentResult, setPaymentResult] = useState<{
        boletoUrl: string | null;
    } | null>(null);
    const [alreadyPaid, setAlreadyPaid] = useState(false);
    const clipboard = useClipboard({ timeout: 2000 });

    const allowedMethods = getFormPaymentMethod(props.invoice?.paymentMethod);
    const deadlineExpired = isPaymentDeadlineExpired(
        props.invoice?.approvedAt,
        props.invoice?.paymentMethod,
    );
    const paymentBlockReason = getInvoicePaymentBlockReason(props.invoice?.status);
    const issuedBoletoUrl =
        props.invoice?.invoicePayments?.find(
            (payment: any) => payment?.mundipaggBoletoUrl,
        )?.mundipaggBoletoUrl ?? null;

    async function handleCopyPaymentLink(): Promise<void> {
        setGeneratingLink(true);

        try {
            const resp = await PaymentSessionService.createSession(props.invoice?.id);

            if (resp.sessionToken) {
                const paymentUrl = `${window.location.origin}/pay/${resp.sessionToken}`;

                clipboard.copy(paymentUrl);
                setCopiedLink({
                    paymentUrl,
                    expiresAt: resp.expiresAt,
                });
                toast.success(
                    'Link de pagamento copiado para a área de transferência!',
                );
            } else {
                toast.error('Erro ao gerar link de pagamento');
            }
        } catch (error: any) {
            handleResponseError(
                error,
                'Ocorreu um erro ao gerar link de pagamento.',
            );
        } finally {
            setGeneratingLink(false);
        }
    }

    async function handlePaymentSubmit(
        data: PaymentMethodsFormSubmitData,
    ): Promise<void> {
        let payments;

        try {
            payments = await buildPaymentsPayload(data);
        } catch (error: any) {
            toast.error(error.message || 'Erro ao preparar dados do pagamento');
            return;
        }

        try {
            const response = await PaymentHttpService.checkoutTokenizedInvoice(
                props.invoice.id,
                payments,
            );
            toast.success('Pagamento realizado com sucesso!');
            setShowPaymentModal(false);
            const boletoUrl = response.data?.mundipaggBoletoUrl || null;
            setPaymentResult({ boletoUrl });
            if (boletoUrl) {
                window.open(boletoUrl, '_blank', 'noreferrer');
            } else {
                setTimeout(() => window.location.reload(), 3000);
            }
        } catch (error: any) {
            const statusCode = error?.statusCode;
            const message = typeof error?.message === 'string' ? error.message : '';

            if (statusCode === PRECONDITION_FAILED && isAlreadyPaidMessage(message)) {
                setShowPaymentModal(false);
                setAlreadyPaid(true);
                setTimeout(() => window.location.reload(), 3000);
            } else if (statusCode === PRECONDITION_FAILED) {
                toast.error(message || 'Cartão recusado. Tente novamente com outro cartão.');
            } else {
                handleResponseError(error, 'Erro ao processar pagamento');
            }
        }
    }

    function renderModalBody() {
        if (alreadyPaid) {
            return (
                <Row className="mt-4 ml-3">
                    <Alert variant="info" className="w-100">
                        <strong>Este pedido já foi pago.</strong>
                        <p className="mb-0 mt-2">
                            Não é necessário realizar o pagamento novamente.
                            Atualizando a página...
                        </p>
                    </Alert>
                </Row>
            );
        }

        if (paymentResult) {
            return (
                <Row className="mt-4 ml-3">
                    <Alert variant="success" className="w-100">
                        <strong>Pagamento realizado com sucesso!</strong>
                        {paymentResult.boletoUrl ? (
                            <p className="mb-0 mt-2">
                                <a
                                    href={paymentResult.boletoUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Ver / baixar boleto
                                </a>
                            </p>
                        ) : (
                            <p className="mb-0 mt-2">Atualizando a página...</p>
                        )}
                    </Alert>
                </Row>
            );
        }

        if (paymentBlockReason) {
            return (
                <Row className="mt-4 ml-3">
                    <Alert variant="warning" className="w-100">
                        <strong>Pagamento indisponível.</strong>
                        <p className="mb-0 mt-2">{paymentBlockReason}</p>
                    </Alert>
                </Row>
            );
        }

        if (deadlineExpired) {
            return (
                <Row className="mt-4 ml-3">
                    <Alert variant="warning" className="w-100">
                        <strong>Prazo de pagamento expirado.</strong>
                        <p className="mb-0 mt-2">
                            O prazo para pagar este pedido já passou. Entre em
                            contato com nosso time comercial.
                        </p>
                    </Alert>
                </Row>
            );
        }

        if (issuedBoletoUrl) {
            return (
                <Row className="mt-4 ml-3">
                    <Alert variant="info" className="w-100">
                        <strong>O boleto deste pedido já foi emitido.</strong>
                        <p className="mb-0 mt-2">
                            <a
                                href={issuedBoletoUrl}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Ver / baixar boleto
                            </a>
                        </p>
                    </Alert>
                </Row>
            );
        }

        return (
            <>
                <Row>
                    <div className="mt-4 ml-3">
                        <StyledPageTitle>
                            Como o cliente vai pagar?
                        </StyledPageTitle>
                        <StyledPageSubTitle>
                            Copie o link e envie para o cliente, ou realize o pagamento agora mesmo.
                        </StyledPageSubTitle>
                    </div>
                </Row>

                <Row className="mt-4 ml-3">
                    <Col xs={12} className="mb-2">
                        <Button
                            variant="outline-primary"
                            className="w-100"
                            disabled={generatingLink}
                            onClick={handleCopyPaymentLink}
                        >
                            {generatingLink ? (
                                <>
                                    <Spinner
                                        animation="border"
                                        size="sm"
                                        className="me-2"
                                    />
                                    Gerando link...
                                </>
                            ) : (
                                <div className="d-flex align-items-center justify-content-center">
                                    <HiOutlineClipboardCopy className="mr-1" size={16} />
                                    Copiar Link de Pagamento
                                </div>
                            )}
                        </Button>
                    </Col>
                    <Col xs={12}>
                        <Button
                            variant="primary"
                            className="w-100"
                            disabled={allowedMethods.length === 0}
                            onClick={() => setShowPaymentModal(true)}
                        >
                            <div className="d-flex align-items-center justify-content-center">
                                <HiOutlineCreditCard className="mr-1" size={16} />
                                Pagar Agora
                            </div>
                        </Button>
                        {allowedMethods.length === 0 && (
                            <small className="text-muted d-block mt-1">
                                Forma de pagamento deste pedido não
                                suportada por este fluxo.
                            </small>
                        )}
                    </Col>
                </Row>

                {copiedLink && (
                    <Row className="mt-3 ml-3">
                        <Alert variant="success" className="w-100">
                            <strong>Link copiado!</strong>
                            <p className="mt-2 mb-1">
                                Válido até:{' '}
                                <strong>
                                    {new Date(
                                        copiedLink.expiresAt,
                                    ).toLocaleString('pt-BR')}
                                </strong>
                            </p>
                            <small>
                                <code>{copiedLink.paymentUrl}</code>
                            </small>
                        </Alert>
                    </Row>
                )}

                <Row>
                    <div className="mt-4 ml-3">
                        <StyledPageTitle>Atenção:</StyledPageTitle>
                        <StyledPageSubTitle>
                            Após receber o link, o cliente terá até a data
                            de expiração para realizar o pagamento de
                            forma segura e rápida.
                        </StyledPageSubTitle>
                    </div>
                </Row>
            </>
        );
    }

    return (
        <>
            <Modal
                {...props}
                show={props.show && !showPaymentModal}
                size="xl"
                centered
            >
                <Modal.Header closeButton onHide={props.onHide}>
                    <Modal.Title className="ml-3">
                        <Row>
                            <Col sm={1}>
                                <BarcodeSvg />
                            </Col>
                            <Col>
                                <div className="ml-2">
                                    Pagamento{' '}
                                    {formatCurrency(props.invoice.totalInvoice)}
                                </div>
                            </Col>
                        </Row>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="ml-3">
                    <Row>
                        <Col lg={6} md={12} sm={12} xs={12}>
                            {renderModalBody()}
                        </Col>
                        <Col lg={6} md={12} sm={12} xs={12}>
                            <InvoiceSummary invoice={props.invoice} />
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="d-block" />
            </Modal>

            <PaymentModal
                show={showPaymentModal}
                onHide={() => setShowPaymentModal(false)}
                totalAmount={Math.round((props.invoice?.totalInvoice || 0) * 100)}
                maxInstallments={props.invoice?.cardMaxInstallmentNumber || 12}
                invoiceNumber={`Pedido #${props.invoice?.id}`}
                allowedMethods={allowedMethods}
                onPaymentSubmit={handlePaymentSubmit}
            />
        </>
    );
};

export default SessionPayment;
